import { DEFAULT_CURRENCY } from '@constants/global';

import { currencyPreferences, numberFormatCache } from './constants';
import { getLocale } from './get-locale';
import { FormatOptions } from './price-formatter.type';

const getCurrencyDisplayOption = (): 'narrowSymbol' | 'code' => {
  return currencyPreferences.display === 'code' ? 'code' : 'narrowSymbol';
};

const createCacheKey = (
  currency: string,
  minFractionDigits: number,
  maxFractionDigits: number,
  shortened: boolean
): string => {
  return `${currencyPreferences.format}-${currencyPreferences.display}-${currencyPreferences.lang}-${currency}-${minFractionDigits}-${maxFractionDigits}-${shortened}`;
};

export const formatCurrency = (options: FormatOptions): string => {
  const currency = options.currency ?? DEFAULT_CURRENCY;

  const minFractionDigits = options.minimumFractionDigits === 0 ? 0 : options.minimumFractionDigits ?? 2;
  const maxFractionDigits =
    options.maximumFractionDigits === undefined
      ? Math.max(2, minFractionDigits)
      : Math.max(options.maximumFractionDigits, minFractionDigits);

  const cacheKey = createCacheKey(currency, minFractionDigits, maxFractionDigits, options.shortened ?? false);

  let formatter = numberFormatCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat(getLocale(), {
      style: 'currency',
      currency,
      currencyDisplay: getCurrencyDisplayOption(),
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      notation: options.shortened ? 'compact' : 'standard',
    });
    numberFormatCache.set(cacheKey, formatter);
  }

  let result: string;

  // By default we preserve the non-breaking spaces that Intl inserts per CLDR
  // rules (NBSP before/after symbol for European locales, always NBSP around
  // ISO codes). These non-breaking spaces are what makes `EUR 1,234.56` legible
  // vs the illegible `EUR1,234.56`.
  //
  // Pass `removeSpaces: true` as an escape hatch for very tight UI where the
  // symbol must be glued to the number.
  if (options.removeSpaces === true) {
    const parts = formatter.formatToParts(options.number);
    result = parts
      .filter((p) => p.type !== 'literal')
      .map((p) => p.value)
      .join('');
  } else {
    result = formatter.format(options.number);
  }

  // Add plus prefix for positive numbers if requested
  if (options.showPlusPrefix && options.number > 0) {
    result = `+${result}`;
  }

  if (options.showNegativePrefix && options.number >= 0) {
    result = `-${result}`;
  }

  return result;
};
